/**
 * Custom React hooks for API calls
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api, ApiError, LlmUsageListParams, AccuracyMonitoringParams, StatusDashboardParams, ViolationTrendParams } from '../services/api';

// Generic API hook
export function useApi<T>(
    apiCall: () => Promise<T>,
    dependencies: any[] = []
) {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Create a stable dependency key to avoid re-creating callbacks every render
    const depsKey = useMemo(() => JSON.stringify(dependencies ?? []), [dependencies]);
    const apiCallRef = useRef(apiCall);
    apiCallRef.current = apiCall;

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            try {
                setLoading(true);
                setError(null);
                const result = await apiCallRef.current();
                if (!cancelled) setData(result);
            } catch (err: unknown) {
                if (!cancelled) {
                    if (err instanceof ApiError) {
                        setError(err.message);
                    } else {
                        setError('An unexpected error occurred');
                    }
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => { cancelled = true; };
    }, [depsKey]);

    const refetch = useCallback(() => {
        // Trigger by changing depsKey path is complex; just invoke directly
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const result = await apiCallRef.current();
                setData(result);
            } catch (err: unknown) {
                if (err instanceof ApiError) {
                    setError(err.message);
                } else {
                    setError('An unexpected error occurred');
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return { data, loading, error, refetch };
}

// Dashboard hooks
export function useDashboardStats() {
    const apiCall = useMemo(() => () => api.dashboard.getStats(), []);
    return useApi(apiCall, []);
}

export function useDashboardActivities(limit?: number) {
    const apiCall = useMemo(() => () => api.dashboard.getActivities(limit), [limit]);
    return useApi(apiCall, [limit]);
}

// Jobs hooks
export function useJobs(
    page?: number,
    limit?: number,
    jobStatuses?: string[],
    timeFilter?: string,
    search?: string,
    client?: string,
    version?: number,
    asp?: string,
    media?: string,
    operationCompany?: string,
    clients?: string[],
    versions?: number[],
    asps?: string[],
    media_names?: string[],
    operation_companies?: string[],
    created_by_ids?: string[],
    device_modes?: string[],
) {
    const apiCall = useMemo(
        () => () => api.jobs.getJobs(page, limit, jobStatuses, timeFilter, search, client, version, asp, media, operationCompany, clients, versions, asps, media_names, operation_companies, created_by_ids, device_modes),
        [page, limit, jobStatuses, timeFilter, search, client, version, asp, media, operationCompany, clients, versions, asps, media_names, operation_companies, created_by_ids, device_modes]
    );
    return useApi(apiCall, [page, limit, jobStatuses, timeFilter, search, client, version, asp, media, operationCompany, clients, versions, asps, media_names, operation_companies, created_by_ids, device_modes]);
}

export function useJobFilterOptions(search?: string) {
    const apiCall = useMemo(() => () => api.jobs.getFilterOptions(search), [search]);
    return useApi(apiCall, [search]);
}

export function useLlmUsage(params: LlmUsageListParams) {
    const depsKey = JSON.stringify(params);
    const apiCall = useMemo(() => () => api.llmUsage.getList(params), [depsKey]);
    return useApi(apiCall, [depsKey]);
}

export function useLlmUsageFilterOptions() {
    const apiCall = useMemo(() => () => api.llmUsage.getFilterOptions(), []);
    return useApi(apiCall, []);
}

export function useLlmUsageTimeseries(bucket: 'hour' | 'day', params: LlmUsageListParams) {
    const depsKey = JSON.stringify({ bucket, ...params });
    const apiCall = useMemo(() => () => api.llmUsage.getTimeseries(bucket, params), [depsKey]);
    return useApi(apiCall, [depsKey]);
}

export function useAccuracyMonitoringTimeseries(params: AccuracyMonitoringParams) {
    const depsKey = JSON.stringify(params);
    const apiCall = useMemo(() => () => api.accuracyMonitoring.getTimeseries(params), [depsKey]);
    return useApi(apiCall, [depsKey]);
}

export function useAccuracyMonitoringJobs(params: AccuracyMonitoringParams) {
    const depsKey = JSON.stringify(params);
    const apiCall = useMemo(() => () => api.accuracyMonitoring.getJobs(params), [depsKey]);
    return useApi(apiCall, [depsKey]);
}

export function useStatusDashboardTimeseries(params: StatusDashboardParams) {
    const depsKey = JSON.stringify(params);
    const apiCall = useMemo(() => () => api.statusDashboard.getTimeseries(params), [depsKey]);
    return useApi(apiCall, [depsKey]);
}

export function useViolationTrendTimeseries(params: ViolationTrendParams) {
    const depsKey = JSON.stringify(params);
    const apiCall = useMemo(() => () => api.violationTrend.getTimeseries(params), [depsKey]);
    return useApi(apiCall, [depsKey]);
}

export function useUrlProcessResults(
    page?: number,
    limit?: number,
    status?: string,
    jobIds?: string[],
    statuses?: string[],
    search?: string,
    timeFilters?: string[],
    clients?: string[],
    versions?: number[],
    asps?: string[],
    mediaNames?: string[],
    operationCompanies?: string[],
    createdByIds?: string[],
    categories?: string[],
) {
    const apiCall = useMemo(
        () => () => api.jobs.getUrlProcessResults(page, limit, status, jobIds, statuses, search, timeFilters, clients, versions, asps, mediaNames, operationCompanies, createdByIds, categories),
        [page, limit, status, jobIds, statuses, search, timeFilters, clients, versions, asps, mediaNames, operationCompanies, createdByIds, categories]
    );
    return useApi(apiCall, [page, limit, status, jobIds, statuses, search, timeFilters, clients, versions, asps, mediaNames, operationCompanies, createdByIds, categories]);
}

// Violations hooks
export function useViolations(page?: number, limit?: number, status?: string, severity?: string, search?: string, jobId?: string, url?: string) {
    const apiCall = useMemo(
        () => () => api.violations.getViolations(page, limit, status, severity, search, jobId, url),
        [page, limit, status, severity, search, jobId, url]
    );
    return useApi(apiCall, [page, limit, status, severity, search, jobId, url]);
}

export function useViolation(violationId: string) {
    const apiCall = useMemo(() => () => api.violations.getViolation(violationId), [violationId]);
    return useApi(apiCall, [violationId]);
}

export function useViolationStats() {
    const apiCall = useMemo(() => () => api.violations.getStats(), []);
    return useApi(apiCall, []);
}

// Mutation hooks (for POST, PUT, DELETE operations)
export function useApiMutation<T, P = any>(
    apiCall: (params: P) => Promise<T>
) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const mutate = useCallback(async (params: P): Promise<T | null> => {
        try {
            setLoading(true);
            setError(null);
            const result = await apiCall(params);
            return result;
        } catch (err: unknown) {
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError('An unexpected error occurred');
            }
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    return { mutate, loading, error };
}

// Specific mutation hooks
export function useJobAction() {
    return useApiMutation(({ jobId, action }: { jobId: string; action: string }) =>
        api.jobs.executeAction(jobId, action)
    );
}

export function useViolationStatusUpdate() {
    return useApiMutation(({ violationId, status, notes }: { violationId: string; status: string; notes?: string }) =>
        api.violations.updateStatus(violationId, status, notes)
    );
}
